import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle'

import { Flex, Icon } from 'components'
import Box from 'components/Box'
import Skeleton from 'components/SkeletonLoader'
import Tooltip from 'components/Tooltip'

import { LeadActivityType } from 'types/global'

import { colors, HexToRgba, ThemeColor } from 'utils/colors'
import { CUT_OFF_HEIGHT, SINGLE_LINE_HEIGHT, SPACE_ADJUSTMENT, SPACE_ADJUSTMENT_FOR_SINGLE_LINE } from './constants'

type IndicatorProps = {
  color: ThemeColor
  text?: string
}

type Props = {
  icon: JSX.Element
  content: JSX.Element
  isOnlyAttachment?: boolean
  useClamp?: boolean
  isActive: boolean
  isLoading?: boolean
  leadActivityType: LeadActivityType
  warning?: string | boolean
  onClickOpenForm?: () => void
  indicator?: IndicatorProps
  onClickCard?: () => void
  bottomContent?: JSX.Element
  isExpanded: boolean
  expandAll: boolean
  onToggle: () => void
  handleTruncationChange: ({ leadActivityId, truncated }: { leadActivityId: string; truncated: boolean }) => void
  leadActivityId: string
  cutOffHeight?: number
  adjustCutoff?: boolean
  isTruncated?: boolean
}

const ActivityIconBaseColor: { [key in LeadActivityType]: keyof typeof colors } = {
  'Lead Lifecycle Event': 'transparent',
  Appointment: 'blue1',
  Call: 'purple1',
  Email: 'blue1',
  'Email (Log)': 'blue1',
  'Live Call': 'orange1',
  Meeting: 'blue1',
  Note: 'red3',
  'To Do': 'green1',
  Message: 'purple1',
}

const ACTIVITY_TOOLTIP: { [key in LeadActivityType]: string } = {
  'Lead Lifecycle Event': 'Life Cycle Event',
  Appointment: 'meeting_lbl',
  Call: 'call_task_lbl',
  Email: 'email_lbl',
  'Email (Log)': 'email_log_lbl',
  'Live Call': 'live_call',
  Meeting: 'meeting_lbl',
  Note: 'note_lbl',
  'To Do': 'todo_task',
  Message: 'call_message_lbl',
}

const ActivityItemWrapper = ({
  icon,
  content,
  isOnlyAttachment,
  isActive,
  isLoading = false,
  leadActivityType,
  warning,
  onClickOpenForm,
  indicator,
  onClickCard,
  bottomContent,
  onToggle,
  isExpanded = false,
  // expandAll, // should be removed from each card
  handleTruncationChange,
  isTruncated = false,
  cutOffHeight = CUT_OFF_HEIGHT,
  leadActivityId,
  adjustCutoff = false,
}: Props) => {
  const { t } = useTranslation()
  const summaryRef = useRef<HTMLDivElement>(null)
  const [canTruncate, setCanTruncate] = useState(isTruncated)
  // track the visibility of card based on virtual list scroll
  const [isVisible, setIsVisible] = useState(false)

  const cardBorder = warning ? colors.red : colors.gray3

  // fixing max height for single lined body to remove extra space.
  const isSingleLine = summaryRef.current?.querySelector('#body')?.clientHeight === SINGLE_LINE_HEIGHT
  const customMaxHeight = isSingleLine ? cutOffHeight - SINGLE_LINE_HEIGHT : cutOffHeight

  // 🔍 Observe visibility (due to tanstack virtual scroll element)
  useEffect(() => {
    const el = summaryRef.current
    if (!el) return

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsVisible(true)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // 📏 Measure truncation *only when visible and content changes
  useEffect(() => {
    const el = summaryRef.current
    if (!el) return

    const resizeObserver = new ResizeObserver(() => {
      if (!isVisible || !el) return

      const truncated =
        isTruncated ||
        el.scrollHeight >
          cutOffHeight + (adjustCutoff ? (isSingleLine ? SPACE_ADJUSTMENT_FOR_SINGLE_LINE : SPACE_ADJUSTMENT) : 0)
      setCanTruncate((prev) => {
        if (isExpanded && truncated === false) {
          return truncated
        }
        if (prev !== truncated) {
          return truncated
        }
        return prev
      })

      if (truncated) {
        handleTruncationChange({ leadActivityId, truncated: true })
      }
    })

    // Observer is to track height change of an element which holds the content
    resizeObserver.observe(el)

    return () => resizeObserver.disconnect()
  }, [isOnlyAttachment, isExpanded, isVisible, leadActivityId, adjustCutoff, cutOffHeight, isSingleLine, isTruncated])

  const toggleMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    onToggle()
  }

  const renderExpandCollapseContent = ({
    children,
    useClamp = true,
  }: {
    children: React.ReactNode
    useClamp?: boolean
  }) => (
    <>
      <Flex
        gap="0"
        flexDirection="column"
        width="100%"
        alignItems="flex-start"
        onClick={onClickCard}
        position="relative"
      >
        <ContentBox ref={summaryRef} isExpanded={isExpanded} useClamp={useClamp} customMaxHeight={customMaxHeight}>
          {children}
        </ContentBox>

        {(canTruncate || isTruncated) && !isExpanded && (
          <Box position="absolute" bottom="-3px" right="0px">
            <InlineToggle onClick={toggleMore}>{t('show_more_lbl')}</InlineToggle>
          </Box>
        )}
        {(canTruncate || isTruncated) && isExpanded && (
          <Flex justifyContent="flex-end" alignItems="center" gap="0" width="100%">
            <InlineToggle onClick={toggleMore}>{t('show_less_lbl')}</InlineToggle>
          </Flex>
        )}
      </Flex>
    </>
  )

  return (
    <Box border={`1px solid ${cardBorder}`} borderRadius="0px 0px 4px 4px">
      <Container
        backgroundColor={isActive ? 'none' : colors.gray1}
        isActive={isActive}
        hasWarning={!!warning}
        cursor="pointer"
        data-testid="test-id-activity-item-wrapper"
      >
        <Flex onClick={onClickCard} alignItems="flex-start" p="8px 16px" gap="8px">
          {isLoading ? (
            <StyledIcon isActive={isActive} isLoading={isLoading} leadActivityType={leadActivityType}>
              <Skeleton />
            </StyledIcon>
          ) : (
            <Tooltip position="bottom" title={ACTIVITY_TOOLTIP[leadActivityType]}>
              <StyledIcon isActive={isActive} isLoading={isLoading} leadActivityType={leadActivityType}>
                {icon}
              </StyledIcon>
            </Tooltip>
          )}
          {renderExpandCollapseContent({ children: content, useClamp: !isOnlyAttachment })}

          {warning && typeof warning === 'string' && (
            <ClickableBox height="16px" onClick={onClickOpenForm}>
              <Tooltip position="bottomRight" textTransform="none" title={warning}>
                <Icon icon={faExclamationTriangle} color="red" />
              </Tooltip>
            </ClickableBox>
          )}
        </Flex>
        {indicator && <Indicator {...indicator} />}
      </Container>
      {bottomContent}
    </Box>
  )
}

const ContentBox = styled(Box)<{ isExpanded?: boolean; useClamp?: boolean; customMaxHeight?: number }>`
  width: 100%;
  overflow: hidden;
  line-height: 20px;

  ${({ useClamp, isExpanded, customMaxHeight }) =>
    useClamp
      ? !isExpanded &&
        `
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      `
      : !isExpanded &&
        `
        max-height:  ${customMaxHeight}px;
        transition: 0.3s ease;
      `}
`

const InlineToggle = styled.button`
  background-color: rgb(255 255 255 / 90%);
  color: ${({ theme }) => theme.colors.blue1};
  border: none;
  cursor: pointer;
  line-height: 24px;
  font-size: 14px;
  font-weight: 500;
`

const StyledIcon = styled.div<{ isActive: boolean; isLoading: boolean; leadActivityType: LeadActivityType }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: ${({ theme, isActive, isLoading, leadActivityType }) =>
    isActive
      ? HexToRgba(isLoading ? theme.colors.gray5 : theme.colors[ActivityIconBaseColor[leadActivityType]], 0.16)
      : HexToRgba(theme.colors.gray5, 0.16)};

  svg {
    color: ${({ theme, isActive, leadActivityType }) =>
      isActive ? theme.colors[ActivityIconBaseColor[leadActivityType]] : theme.colors.gray5};
  }
`

const Container = styled(Box)<{ isActive: boolean; hasWarning: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 4px;

  &:hover {
    border-color: ${({ theme, isActive, hasWarning }) => {
      if (isActive && hasWarning) return theme.colors.red
      if (isActive && !hasWarning) return theme.colors.blue2
      return theme.colors.gray3
    }};
  }
`

const ClickableBox = styled(Box)`
  cursor: pointer;
`

const Indicator = ({ color, text }: IndicatorProps) => (
  <IndicatorContainer>
    <Tooltip title={text} position="topLeft">
      <Triangle color={color} />
    </Tooltip>
  </IndicatorContainer>
)

const IndicatorContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 1rem;
  height: 1rem;
  border-radius: 0 0 0 4px;
  z-index: 1;
`

const Triangle = styled.div<{ color: ThemeColor }>`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 1rem 1rem 0;
  border-color: transparent transparent ${({ theme, color }) => theme.colors[color]} transparent;
  border-radius: 0 0 0 3px;
`

export { ACTIVITY_TOOLTIP, ActivityItemWrapper }
